import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, ExternalLink, Sparkles, Users2, ArrowRight, Download, Eye } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { communityData } from '../data/community';
import { BilingualFlyer } from '../components/BilingualFlyer';

const categoryColors = {
  Event: { bg: 'bg-rose-500', text: 'text-white' },
  Community: { bg: 'bg-violet-500', text: 'text-white' },
  Advocacy: { bg: 'bg-amber-500', text: 'text-white' },
  Initiative: { bg: 'bg-teal-500', text: 'text-white' }
};

// Community partners
const communityPartners = [
  "Office of the State Superintendent of Education (OSSE)",
  "DC Public Schools",
  "DC Child Care Connections",
  "Under 3 DC Coalition",
  "DC Health Benefit Exchange Authority (HBX)"
];

const CommunityPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Hero Section */}
      <section id="main-content" role="main" tabIndex={-1} className="pt-32 pb-16 bg-gradient-to-br from-violet-900 via-purple-800 to-violet-900 relative overflow-hidden">
        {/* Background Decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-20 w-40 h-40 border border-white/10 rounded-full" />
          <div className="absolute bottom-20 right-20 w-60 h-60 border border-white/10 rounded-full" />
          <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-violet-300 hover:text-violet-200 mb-8 transition-colors duration-300"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="flex items-center gap-4 mb-6">
            <span className="px-4 py-2 bg-violet-500/20 text-violet-300 rounded-full text-sm font-semibold">
              Community Engagement
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            In The Community
          </h1>
          <p className="text-xl text-violet-200 max-w-3xl leading-relaxed">
            Discover how DCELC connects, supports, and strengthens the early childhood education community across Washington, DC through events, partnerships, and initiatives.
          </p>
        </div>
      </section>

      {/* Community Initiatives — landing sections */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 space-y-16">
        {[
          {
            id: 'faith-ucc',
            number: '01',
            title: 'Faith UCC Mobile Market',
            tagline: '3rd Thursday monthly · 4900 10th St NE, Washington, DC 20017',
            intro: 'Free fresh produce, bread, pastries, and diapers for babies and adults — a monthly community market hosted at Faith United Church of Christ. Bring your bags and invite family, friends, and neighbors!',
            href: '/initiatives/faith-ucc-mobile-market',
            cta: 'Explore the Mobile Market',
            badge: 'bg-violet-100 text-violet-700',
            btn: 'bg-violet-600 hover:bg-violet-700',
            num: 'text-violet-200',
            panel: 'from-violet-900 via-purple-900 to-slate-900',
            image: 'https://customer-assets.emergentagent.com/job_548e8c1f-8aee-47f4-a78f-1f6376525bd5/artifacts/hj0febw4_unnamed%20%282%29.png'
          }
        ].map((init, index) => (
          <section
            key={init.id}
            aria-labelledby={`initiative-${init.id}-title`}
            data-testid={`initiative-section-${init.id}`}
            className="bg-white rounded-3xl shadow-lg border border-slate-100 overflow-hidden"
          >
            <div className={`grid grid-cols-1 lg:grid-cols-2 ${index % 2 === 1 ? 'lg:[direction:rtl]' : ''}`}>
              <div className={`relative min-h-[260px] lg:min-h-[340px] bg-gradient-to-br ${init.panel} [direction:ltr]`}>
                <img src={init.image} alt={init.title} className="absolute inset-0 w-full h-full object-cover" />
                <span className={`absolute top-6 left-6 text-6xl font-black ${init.num} opacity-90 drop-shadow-lg`} aria-hidden="true" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {init.number}
                </span>
              </div>
              <div className="p-8 sm:p-12 flex flex-col justify-center [direction:ltr]">
                <span className={`inline-flex items-center gap-2 self-start px-3 py-1 ${init.badge} rounded-full text-xs font-bold uppercase tracking-wider mb-4`}>
                  <Users2 className="w-3.5 h-3.5" aria-hidden="true" />
                  Community Initiative {init.number}
                </span>
                <h2 id={`initiative-${init.id}-title`} className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">
                  {init.title}
                </h2>
                <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">{init.tagline}</p>
                <p className="text-slate-600 leading-relaxed mb-8">{init.intro}</p>
                <div>
                  <Link
                    to={init.href}
                    data-testid={`initiative-cta-${init.id}`}
                    className={`inline-flex items-center gap-2 px-6 py-3 ${init.btn} text-white font-semibold rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500`}
                  >
                    {init.cta}
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          </section>
        ))}
      </div>

      {/* Upcoming Events Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-10">
            <Sparkles className="w-6 h-6 text-violet-600" />
            <h2 className="text-2xl font-bold text-slate-800">Community Events</h2>
          </div>
          <p className="text-slate-600 mb-10">Join us at these upcoming community activities</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {communityData.events.filter((event) => !event.link?.startsWith('/initiatives')).map((event) => {
              const colors = categoryColors[event.category] || categoryColors.Event;
              
              return (
                <div 
                  key={event.id}
                  className={`bg-slate-50 rounded-3xl overflow-hidden shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-300 group ${event.subEvents ? 'col-span-1 md:col-span-2' : ''}`}
                >
                  {/* Event Image */}
                  <div className={`relative overflow-hidden ${event.imageEs ? 'h-80' : 'h-52'}`}>
                    {event.imageEs ? (
                      <BilingualFlyer imageEn={event.image} imageEs={event.imageEs} alt={event.title} />
                    ) : (
                      <img
                        src={event.image}
                        alt={event.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    )}
                    {/* Category Badge */}
                    <span className={`absolute top-4 left-4 px-3 py-1 ${colors.bg} ${colors.text} rounded-full text-xs font-semibold`}>
                      {event.category}
                    </span>
                    {/* Logo overlay if available */}
                    {event.logo && (
                      <div className="absolute bottom-4 right-4 bg-white rounded-lg p-2 shadow-lg">
                        <img src={event.logo} alt={event.organizer} className="h-12 w-auto" />
                      </div>
                    )}
                  </div>
                  
                  {/* Event Info */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-violet-600 transition-colors duration-300">
                      {event.link && !event.subEvents ? (
                        <a href={event.link} target="_blank" rel="noopener noreferrer" className="hover:underline">
                          {event.title}
                        </a>
                      ) : (
                        event.title
                      )}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-4">
                      {event.description}
                    </p>
                    
                    {/* Organizer Info (if available and no subEvents) */}
                    {event.organizer && !event.subEvents && (
                      <div className="bg-white rounded-lg p-3 mb-4 border border-slate-200">
                        <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Presented by</p>
                        <p className="text-sm font-medium text-slate-700">{event.organizer}</p>
                        {event.contact && (
                          <p className="text-xs text-slate-500 mt-1">{event.contact}</p>
                        )}
                      </div>
                    )}
                    
                    {/* Sub-Events (for DC CCC style blocks) */}
                    {event.subEvents && (
                      <div className="space-y-4 mb-4">
                        {event.subEvents.map((subEvent, subIndex) => (
                          <div key={subIndex} className="bg-white rounded-xl p-4 border border-slate-200">
                            <h4 className="font-bold text-slate-800 mb-2">{subEvent.title}</h4>
                            <p className="text-slate-600 text-sm mb-3">{subEvent.description}</p>
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <div className="flex items-center gap-2 text-slate-500 text-sm">
                                <Calendar className="w-4 h-4 text-violet-500" />
                                <span>{subEvent.date}</span>
                              </div>
                              {subEvent.link && (
                                <a 
                                  href={subEvent.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-full text-sm font-medium hover:bg-violet-700 transition-colors"
                                >
                                  {subEvent.linkLabel || 'Register'}
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Contact Info for events with subEvents */}
                    {event.subEvents && event.contact && (
                      <div className="bg-slate-100 rounded-lg p-3 mb-4">
                        <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Contact {event.organizer}</p>
                        <p className="text-sm text-slate-600">{event.contact}</p>
                      </div>
                    )}
                    
                    {/* Date & Location (only for non-subEvents) */}
                    {!event.subEvents && (
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-slate-500 text-sm">
                          <Calendar className="w-4 h-4 text-violet-500" />
                          <span>{event.date}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-500 text-sm">
                          <MapPin className="w-4 h-4 text-violet-500" />
                          <span>{event.location}</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Learn More Link (only for non-subEvents) */}
                    {event.link && !event.subEvents && (
                      event.link.startsWith('/') ? (
                        <Link 
                          to={event.link}
                          aria-label={`Learn more about ${event.title}`}
                          className="inline-flex items-center gap-2 text-violet-600 font-medium text-sm hover:text-violet-700 transition-colors duration-300"
                        >
                          Learn More
                          <ArrowRight className="w-4 h-4" />
                        </Link>
                      ) : (
                        <a 
                          href={event.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${event.organizer ? 'Register for' : 'Learn more about'} ${event.title} (opens in a new tab)`}
                          className="inline-flex items-center gap-2 text-violet-600 font-medium text-sm hover:text-violet-700 transition-colors duration-300"
                        >
                          {event.organizer ? 'Register Now' : 'Learn More'}
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )
                    )}

                    {/* PDF View / Download */}
                    {event.pdf && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <a
                          href={event.pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`View ${event.title} (opens PDF in a new tab)`}
                          data-testid={`community-view-pdf-${event.id}`}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-full text-sm font-semibold transition-colors duration-300"
                        >
                          <Eye className="w-4 h-4" />
                          View Memo
                        </a>
                        <a
                          href={event.pdf}
                          download="Under-3-DC-Mayoral-Transition-Memo-Sept-2026.pdf"
                          aria-label={`Download ${event.title} PDF`}
                          data-testid={`community-download-pdf-${event.id}`}
                          className="inline-flex items-center gap-2 px-4 py-2 border border-violet-600 text-violet-700 hover:bg-violet-50 rounded-full text-sm font-semibold transition-colors duration-300"
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Community Partners Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-10">
            <Users2 className="w-6 h-6 text-teal-600" />
            <h2 className="text-2xl font-bold text-slate-800">Community Partners</h2>
          </div>
          <p className="text-slate-600 mb-10">
            We collaborate with organizations across the District to strengthen the early childhood ecosystem and support families.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {communityPartners.map((partner, index) => (
              <div 
                key={index}
                className="bg-slate-50 rounded-xl p-6 text-center border border-slate-200 hover:border-teal-300 hover:bg-teal-50 transition-all duration-300"
              >
                <p className="text-slate-700 font-medium">{partner}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Get Involved CTA */}
      <section className="py-20 bg-gradient-to-br from-violet-600 to-purple-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Get Involved in Our Community
          </h2>
          <p className="text-violet-200 mb-8 text-lg">
            Whether you're an educator, parent, or community member, there are many ways to support early childhood education in DC.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/#contact"
              className="px-8 py-4 bg-white text-violet-700 font-semibold rounded-full hover:bg-violet-50 transition-all duration-300"
            >
              Contact Us
            </Link>
            <Link
              to="/programs/stronger-together"
              className="px-8 py-4 bg-violet-500/30 text-white font-semibold rounded-full hover:bg-violet-500/40 border border-white/30 transition-all duration-300"
            >
              Take Action
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CommunityPage;
